import React from "react";
import { PhoneCall, Headphones, Check } from "lucide-react";

const SERVICES = [
  {
    icon: PhoneCall,
    name: "Cold Calling & Appointment Setting",
    points: [
      "Outbound calls to targeted prospects",
      "Lead qualification and appointment booking",
      "Script-based and results-driven approach",
      "Daily call tracking and reporting",
    ],
  },
  {
    icon: Headphones,
    name: "Customer Service Support",
    points: [
      "Phone, email, and chat support",
      "Customer follow-ups and issue resolution",
      "Professional and trained agents",
      "Reliable communication handling",
    ],
  },
];

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      style={{
        position: "relative",
        padding: "96px 0 144px",
        background: "rgba(8,8,8,0.70)",
        overflow: "hidden",
      }}
    >
      {/* Decorative background elements */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "1px", background: "linear-gradient(90deg, transparent, rgba(210,170,60,0.2), transparent)" }} />
      <div
        style={{
          position: "absolute",
          top: "-240px",
          right: "-100px",
          width: "600px",
          height: "600px",
          borderRadius: "50%",
          filter: "blur(150px)",
          background: "radial-gradient(circle, rgba(210,170,60,0.08) 0%, transparent 70%)",
          animation: "hero-orb-2 20s ease-in-out infinite",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-160px",
          left: "-80px",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          filter: "blur(120px)",
          background: "radial-gradient(circle, rgba(139,111,30,0.06) 0%, transparent 70%)",
          animation: "hero-orb-1 25s ease-in-out infinite",
        }}
      />

      <div style={{ position: "relative", maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        {/* Section header */}
        <div style={{ maxWidth: "640px", marginBottom: "64px" }}>
          <div
            className="reveal"
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "10px",
              letterSpacing: "0.4em",
              color: "rgba(210,170,60,0.8)",
              textTransform: "uppercase",
              marginBottom: "20px",
            }}
          >
            What We Do
          </div>
          <h2
            data-testid="services-headline"
            className="reveal reveal-delay-1"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(36px, 5vw, 60px)",
              fontWeight: 300,
              lineHeight: 1.08,
              color: "#fff",
              letterSpacing: "0.02em",
              marginBottom: "24px",
            }}
          >
            Two services.{" "}
            <em style={{ color: "#e8c45a", fontStyle: "italic", fontWeight: 400 }}>One outcome — growth.</em>
          </h2>
          <p
            className="reveal reveal-delay-2"
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "14px",
              fontWeight: 300,
              color: "rgba(220,195,140,0.6)",
              lineHeight: 1.8,
              letterSpacing: "0.02em",
            }}
          >
            Simple, focused, and accountable. We deliver the outbound and support
            operations your business needs to scale.
          </p>
        </div>

        {/* Service cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "32px" }}>
          {SERVICES.map((s, i) => (
            <div
              key={s.name}
              data-testid={`service-card-${i}`}
              className="reveal"
              style={{
                position: "relative",
                padding: "48px 40px",
                background: "rgba(12,12,12,0.8)",
                border: "1px solid rgba(255,255,255,0.08)",
                transition: "all 0.4s ease",
                animationDelay: `${i * 120}ms`,
                cursor: "default",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "rgba(210,170,60,0.35)";
                e.currentTarget.style.background = "rgba(14,14,14,0.9)";
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 20px 60px rgba(0,0,0,0.4), 0 0 40px rgba(210,170,60,0.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                e.currentTarget.style.background = "rgba(12,12,12,0.8)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {/* Top gold line on hover */}
              <div style={{
                position: "absolute",
                top: 0,
                left: 40,
                right: 40,
                height: "1px",
                background: "linear-gradient(90deg, transparent, rgba(210,170,60,0.4), transparent)",
                opacity: 0,
                transition: "opacity 0.4s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
              />

              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "32px" }}>
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    border: "1px solid rgba(210,170,60,0.25)",
                    background: "rgba(210,170,60,0.04)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <s.icon style={{ width: "24px", height: "24px", color: "#e8c45a" }} />
                </div>
                <span
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "10px",
                    letterSpacing: "0.3em",
                    color: "rgba(200,160,50,0.3)",
                    textTransform: "uppercase",
                  }}
                >
                  0{i + 1}
                </span>
              </div>

              <h3
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "28px",
                  fontWeight: 400,
                  color: "#fff",
                  lineHeight: 1.2,
                  letterSpacing: "0.01em",
                  marginBottom: "24px",
                }}
              >
                {s.name}
              </h3>

              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "14px" }}>
                {s.points.map((p) => (
                  <li key={p} style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                    <Check style={{ flexShrink: 0, width: "16px", height: "16px", color: "#e8c45a", marginTop: "2px" }} />
                    <span
                      style={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "13px",
                        fontWeight: 300,
                        color: "rgba(255,255,255,0.65)",
                        lineHeight: 1.6,
                        letterSpacing: "0.01em",
                      }}
                    >
                      {p}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
